'use client';

import { useMemo } from 'react';
import type { HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';

import { getHotpadsUserToken } from '@/src/lib/comscore/windowParams';
import { useAdTags } from '@/src/lib/hooks/query/useAdTagsQuery';
import { useCurrentArea } from '@/src/lib/hooks/query/useCurrentArea';
import { getPis } from './pis';
import { buildSrpAdTargeting, type SrpAdTargeting } from './targeting';

// Legacy targets the zips of the first five results.
const ZIP_TARGETING_LISTING_COUNT = 5;

type TargetingRange = { min?: number | null; max?: number | null };

/** The filter fields ad targeting reads. Both the V3 request and `resolved` filters satisfy it. */
type TargetingFilters = {
  areaId?: string | number | null;
  scope?: HpSearchScopeDto | null;
  boundingBox?: { minLat?: number | null; maxLat?: number | null; minLon?: number | null; maxLon?: number | null };
  price?: TargetingRange;
  beds?: TargetingRange;
  baths?: TargetingRange;
};

type UseSrpAdTargetingParams = {
  /** The live V3 request's filters — the viewport actually being searched. */
  requestFilters?: TargetingFilters;
  /** `resolved.effectiveFilters`: the request's filters plus the slug's own (e.g. 2-bedroom). */
  resolvedFilters?: TargetingFilters;
  listings: ReadonlyArray<{ address?: { zip?: string } }>;
  enabled: boolean;
  /** From `getAdListingType`; kept in sync with the ad unit path's segment. */
  adListingType: string;
};

/**
 * Assembles the GAM key/values for the SRP's ad slots. Returns null until
 * they're ready — `useSrpGoogleAds` holds the ad request until then, since keys
 * applied after `display()` would miss it.
 */
export function useSrpAdTargeting({
  requestFilters,
  resolvedFilters,
  listings,
  enabled,
  adListingType,
}: UseSrpAdTargetingParams): SrpAdTargeting | null {
  const { data: area } = useCurrentArea();

  const boundingBox = requestFilters?.boundingBox ?? resolvedFilters?.boundingBox;
  const price = resolvedFilters?.price ?? requestFilters?.price;
  const beds = resolvedFilters?.beds ?? requestFilters?.beds;
  const baths = resolvedFilters?.baths ?? requestFilters?.baths;
  const isAreaBorderActive =
    (requestFilters?.areaId ?? resolvedFilters?.areaId) != null ||
    (requestFilters?.scope ?? resolvedFilters?.scope) != null;

  const adTagsRequest = useMemo(() => {
    if (!enabled) return null;
    const { minLat, maxLat, minLon, maxLon } = boundingBox ?? {};
    if (minLat == null || maxLat == null || minLon == null || maxLon == null) return null;

    return {
      minLat,
      maxLat,
      minLon,
      maxLon,
      ...(price?.min == null ? {} : { lowPrice: price.min }),
      ...(price?.max == null ? {} : { highPrice: price.max }),
    };
  }, [enabled, boundingBox, price?.min, price?.max]);

  const { data: adTags, isPending: isAdTagsPending } = useAdTags(adTagsRequest);

  const zips = useMemo(
    () =>
      listings
        .slice(0, ZIP_TARGETING_LISTING_COUNT)
        .map((listing) => listing.address?.zip)
        .filter((zip): zip is string => !!zip),
    [listings],
  );

  // adtags supplies dma/cnty and overrides the locally derived state and price
  // range, so wait for it to settle — an error resolves it just as well as data.
  const isWaitingForAdTags = adTagsRequest !== null && isAdTagsPending;

  return useMemo(() => {
    if (!enabled || isWaitingForAdTags) return null;

    return buildSrpAdTargeting({
      area: area && { city: area.city, state: area.state, coordinates: area.coordinates },
      price,
      beds,
      baths,
      zips,
      adTags,
      isAreaBorderActive,
      pis: getPis(),
      userToken: getHotpadsUserToken(),
      adListingType,
    });
  }, [adListingType, adTags, area, baths, beds, enabled, isAreaBorderActive, isWaitingForAdTags, price, zips]);
}
